"use client";

import React, { useState, useRef } from 'react';
import { useAuthenticatedAction } from '@/hooks/useAuthenticatedAction';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Loader2, PaperclipIcon, X } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';
import { QuickActionButtons } from './QuickActionButtons';
import { useIsMobile } from '@/hooks/use-mobile';

interface CenteredChatInputProps {
  onMessageSent: () => void;
}

export const CenteredChatInput: React.FC<CenteredChatInputProps> = ({ onMessageSent }) => {
  const {
    inputMessage,
    setInputMessage,
    sendMessage,
    isResponding,
    selectedFiles,
    removeFile,
    handleFileUpload,
    isUploading,
    openLastConversation,
    openSidebar,
  } = useAuthenticatedAction();
  const { t } = useTranslation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const handleSendMessage = () => {
    if (!inputMessage.trim() || isResponding) return;

    setIsTransitioning(true);

    sendMessage(inputMessage.trim());
    onMessageSent();
  };

  const handleRecommendationClick = (recommendation: string) => {
    setIsTransitioning(true);

    sendMessage(recommendation);
    onMessageSent();
  };

  const handleOpenLastConversation = () => {
    setIsTransitioning(true);
    // First activate the last conversation so context has an activeChat
    openLastConversation();
    // Then tell the parent to exit the centered view
    onMessageSent();
    if (isMobile) {
      openSidebar();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // On mobile, Enter creates a line break (natural behavior)
    // On desktop, Enter sends the message
    if (e.key === 'Enter' && !isMobile && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      console.log('[CenteredChatInput] handleFileInputChange exiting: no files provided');
      return;
    }

    const clonedFiles = Array.from(files);
    console.log('[CenteredChatInput] handleFileInputChange cloning files', { count: clonedFiles.length });
    setIsTransitioning(true);
    handleFileUpload(clonedFiles);
    onMessageSent();

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canSendMessage = !isResponding && inputMessage.trim().length > 0;

  return (
    <>
      {/* Lovable-style vibrant gradient background */}
      <div className="fixed inset-0 z-10 overflow-hidden pointer-events-none flex items-center justify-center">
        <div className="w-[120%] md:w-[800px] aspect-square bg-gradient-radial from-neutral-800/40 via-neutral-900/20 to-transparent blur-2xl translate-y-16" />
      </div>

      <div className={`relative z-20 flex flex-col items-center justify-center h-full transition-all duration-500 ease-in-out ${isTransitioning ? 'transform translate-y-24 opacity-0 scale-95' : ''
        }`}>
        <div className="flex flex-col items-center mb-8 px-2">
          <div className="flex items-center justify-center bg-transparent">
            <img src="/images/lema-logo.png" alt="Lema Logo" className="w-24 mb-4" style={{ background: 'none' }} />
          </div>
          <h3 className="text-2xl text-white mb-2 text-center">
            {t('chat.title')}
          </h3>
          <QuickActionButtons
            onSelect={handleRecommendationClick}
            onOpenLastConversation={handleOpenLastConversation}
          />
        </div>

        <div className="relative w-full max-w-2xl mx-auto px-4">
          {selectedFiles && selectedFiles.length > 0 && (
            <div className="mb-2">
              <div className="flex flex-wrap gap-2 justify-center">
                {selectedFiles.map(file => (
                  <div key={file.id} className="flex items-center p-1.5 rounded-md bg-white/10 backdrop-blur-sm text-xs">
                    <PaperclipIcon className="h-3.5 w-3.5 mr-1.5 text-neutral-300 flex-shrink-0" />
                    <span className="truncate max-w-[150px] text-neutral-300" title={file.name}>{file.name}</span>
                    <button className="ml-1.5 text-neutral-400 hover:text-red-400" onClick={() => removeFile(file.id)} title={t('remove_file')}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="relative">
            <div className="message-input-wrapper">
              <label
                htmlFor="file-upload-centered"
                className={`p-2.5 mt-[1px] transition-colors ${isResponding || isUploading ? 'text-neutral-400 cursor-not-allowed' : 'text-neutral-300 hover:text-white cursor-pointer'
                  }`}
                title={isUploading ? t('uploading_files') : t('attach_files')}
              >
                {isUploading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <PaperclipIcon className="h-5 w-5" />
                )}
              </label>
              <input
                id="file-upload-centered"
                type="file"
                ref={fileInputRef}
                className="hidden"
                multiple
                onChange={handleFileInputChange}
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,video/*"
                disabled={isResponding || isUploading}
              />
              <TextareaAutosize
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('chat.placeholder')}
                className="message-input-textarea"
                rows={1}
                maxRows={4}
              />
              <button
                className={`p-2 mr-2 transition-colors rounded-full animate-pulse ${canSendMessage
                  ? 'bg-highlight/80 hover:bg-highlight text-highlight-foreground'
                  : 'bg-highlight/40 text-highlight-foreground cursor-not-allowed'
                  }`}
                onClick={handleSendMessage}
                disabled={!canSendMessage}
              >
                <ArrowUp className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {!isMobile && isResponding && (
          <div className="mt-6 text-center">
            <p className="text-sm text-neutral-400">
              {t('assistant_thinking')}
            </p>
          </div>
        )}
      </div>
    </>
  );
};
